import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useMemo } from 'react';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import WhiteLabelPage from './pages/WhiteLabelPage';
import { CherrySpinner } from './components/CherryMark';
import Dashboard from './pages/Dashboard';
import StudentProfilePage from './pages/StudentProfilePage';
import AnalyticsPage from './pages/AnalyticsPage';
import TemplatePage from './pages/TemplatePage';
import ResourceLibrary from './pages/ResourceLibrary';
import VideoLibrary from './pages/VideoLibrary';
import TaskBoard from './pages/TaskBoard';
import AIPage from './pages/AIPage';
import DictionaryPage from './pages/DictionaryPage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import useStore from './store/useStore';
import { createAppTheme } from './theme';
import ErrorBoundary from './components/ErrorBoundary';
import PdfViewerHost from './components/pdf/PdfViewerHost';
import { installPdfInterceptor } from './components/pdf/pdfViewer';
import DictationHotkey from './components/voice/DictationHotkey';

// Heavy pages — loaded on first visit
const TodayPage = lazy(() => import('./pages/TodayPage'));
const AssistantPage = lazy(() => import('./pages/AssistantPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DocumentEditorPage = lazy(() => import('./pages/DocumentEditorPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const SalaryPage = lazy(() => import('./pages/SalaryPage'));
const ImagesPage = lazy(() => import('./pages/ImagesPage'));
const SongsPage = lazy(() => import('./pages/SongsPage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
const AiCostsPage = lazy(() => import('./pages/AiCostsPage'));
const TalkPage = lazy(() => import('./pages/TalkPage'));
const LinksPage = lazy(() => import('./pages/LinksPage'));
const MemoryPage = lazy(() => import('./pages/MemoryPage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const PacketPage = lazy(() => import('./pages/PacketPage'));
const PacketLinkPage = lazy(() => import('./pages/PacketLinkPage'));

function PageFallback() {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CherrySpinner size={40} />
    </Box>
  );
}

// a path inside the app to return to after login (?next=), never another site
const safeNext = (v) =>
  typeof v === 'string' && /^\/(?!\/)/.test(v) && !v.startsWith('/login')
    ? v
    : null;

// not logged in → the login page, then back here (packet links /p/<code>)
function ToLogin() {
  const loc = useLocation();
  return (
    <Navigate
      to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`}
      replace
    />
  );
}

function LoginRoute({ loggedIn }) {
  const loc = useLocation();
  if (!loggedIn) return <LoginPage />;
  const next = safeNext(new URLSearchParams(loc.search).get('next'));
  return <Navigate to={next || '/'} replace />;
}

function App() {
  const { loggedIn, userRole, checkSession, darkMode, language } = useStore();
  const staff = loggedIn && (userRole === 'teacher' || userRole === 'admin');
  const [checked, setChecked] = useState(false);
  const theme = useMemo(
    () => createAppTheme(darkMode ? 'dark' : 'light'),
    [darkMode]
  );

  useEffect(() => {
    async function check() {
      await checkSession();
      setChecked(true);
    }
    check();
  }, [checkSession]);

  // installed app (no browser bar): PDFs open in Core's viewer with «Назад»
  useEffect(() => installPdfInterceptor(), []);

  if (!checked) {
    return <CherrySpinner fullScreen />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PdfViewerHost />
        {/* hold right Ctrl anywhere to dictate (staff: the voice API is theirs) */}
        {staff && <DictationHotkey en={language === 'en'} />}
        <BrowserRouter>
          <Routes>
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/partners" element={<WhiteLabelPage />} />
            <Route path="/login" element={<LoginRoute loggedIn={loggedIn} />} />
            <Route
              path="/"
              element={
                loggedIn ? (
                  <Dashboard userRole={userRole} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="/dashboard" element={<Navigate to="/" />} />
            <Route
              path="/student/:id"
              element={
                loggedIn ? (
                  <StudentProfilePage userRole={userRole} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/analytics"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <AnalyticsPage />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/resources"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <ResourceLibrary />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/videos"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <VideoLibrary />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/templates"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <TemplatePage />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/tasks"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <TaskBoard />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/ai"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <AIPage />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/today"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <TodayPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/assistant"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <AssistantPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/memory"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <MemoryPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/inbox"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <InboxPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <ToLogin />
                )
              }
            />
            <Route
              path="/packet/:id"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <PacketPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <ToLogin />
                )
              }
            />
            <Route
              path="/p/:token"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <PacketLinkPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <ToLogin />
                )
              }
            />
            <Route
              path="/links"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <LinksPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/talk"
              element={
                staff ? (
                  <Suspense fallback={<PageFallback />}>
                    <TalkPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/dictionary"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <DictionaryPage />
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/calendar"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <CalendarPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/images"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <ImagesPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/learn"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <LearnPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/songs"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <SongsPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/ai-costs"
              element={
                loggedIn && userRole === 'admin' ? (
                  <Suspense fallback={<PageFallback />}>
                    <AiCostsPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/salary"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <SalaryPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/documents"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <DocumentsPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/documents/:id"
              element={
                loggedIn && (userRole === 'teacher' || userRole === 'admin') ? (
                  <Suspense fallback={<PageFallback />}>
                    <DocumentEditorPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/settings"
              element={
                loggedIn && userRole === 'admin' ? (
                  <Suspense fallback={<PageFallback />}>
                    <SettingsPage />
                  </Suspense>
                ) : loggedIn ? (
                  <UnauthorizedPage />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
